import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { getSaved } from '../api/social';
import PostCard from '../components/PostCard';
import { Bookmark, Camera } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Phase 50 · Favorites page.
 *
 * Lists all posts the current user has saved (bookmark icon on the feed).
 * Re-uses the existing `/api/saved` endpoint. Removing a favorite is
 * handled by the PostCard's built-in toggle — we keep the list in sync
 * by listening to the same global `nymia:data-changed` broadcast that
 * the feed uses for optimistic refresh.
 */
const FavoritesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await getSaved();
      setItems(Array.isArray(rows) ? rows : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    load();
    const onChanged = () => load();
    window.addEventListener('nymia:data-changed', onChanged);
    return () => window.removeEventListener('nymia:data-changed', onChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="mx-auto flex max-w-[975px] gap-8 px-0 pt-4 md:px-4" data-testid="favorites-page">
      <main className="mx-auto w-full max-w-[520px]">
        <div className="mb-2 px-3 md:px-0"><BackButton /></div>
        <header className="mb-4 flex items-center gap-3 px-3 md:px-0">
          <div className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-400 gold-text">
            <Bookmark className="h-6 w-6" strokeWidth={1.6} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Preferiti</h1>
            <p className="text-sm text-muted-foreground">
              I post che hai salvato dal feed.
            </p>
          </div>
        </header>

        {loading ? (
          <div className="flex justify-center py-14">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-amber-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-border bg-card px-6 py-14 text-center" data-testid="favorites-empty">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-amber-400">
              <Camera className="h-8 w-8 gold-text" strokeWidth={1.4} />
            </div>
            <h3 className="text-xl font-semibold">Non hai ancora salvato nessun post.</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Tocca l&apos;icona segnalibro sotto un post per ritrovarlo qui.
            </p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 text-sm font-semibold gold-text hover:underline"
            >
              Esplora il feed
            </button>
          </div>
        ) : (
          <div className="space-y-0">
            {items.map((p) => (
              <PostCard
                key={p.id}
                post={{ ...p, saved_by_me: true }}
                onHidden={(pid) => setItems((prev) => prev.filter((x) => x.id !== pid))}
                onUpdated={(up) => setItems((prev) => prev.map((x) => (x.id === up.id ? { ...up, saved_by_me: true } : x)))}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default FavoritesPage;
